import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  obtenerEstudiantesConAsistencia,
  exportarEstudiantesConAsistencia,
} from "../../../redux/solicitudesAdmSlice";
import { OrderTable } from "../../../Components/OrderTable";
import { ObtenerTodos } from "../../../redux/periodosSlice";
import { getAdminRattendancePage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { Skeleton } from "antd";
import { _translateValue } from "../../translation-gestor/translateValue";

const SCREEN_NAME = "PantallaEstudiantesConAsistencia";

export default function EstudiantesConAsistencia(props) {
  const dispatch = useDispatch();
  const [periodoId, setPeriodoId] = useState(0);
  const [anio, setAnio] = useState(0);
  const [periodos, setPeriodos] = useState([]);
  const [EstudiantesConAsistencia, setEstudiantesConAsistencia] = useState([]);
  const { locale } = useLocale();
  const [adminRAttendancePage, setAdminRAttendancePage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [translatedApoyos, setTranslatedApoyos] = useState({});
  const [translatedCondicionesMedicas, setTranslatedCondicionesMedicas] = useState({});
  const [translatedCarreras, setTranslatedCarreras] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const columns = useMemo(() => {
    const table = adminRAttendancePage?.attendance_table ?? [];
    return [
      { label: table[0]?.label || "EstudianteId", accessor: "estudianteId", sortable: true },
      { label: table[1]?.label || "Estudiante", accessor: "estudiante", sortable: true },
      { 
        label: table[2]?.label || "Apoyo", 
        accessor: "apoyoEstudiosNombre", 
        sortable: true,
        render: (item) => (
          <td>{translatedApoyos[item.apoyoEstudiosNombre] || item.apoyoEstudiosNombre}</td>
        ),
      },
      { 
        label: table[3]?.label || "Condición médica", 
        accessor: "condicionMedica", 
        sortable: true,
        render: (item) => (
          <td>{translatedCondicionesMedicas[item.condicionMedica] || item.condicionMedica}</td>
        ),
      },
      { 
        label: table[4]?.label || "Carrera", 
        accessor: "carrera", 
        sortable: true,
        render: (item) => (
          <td>{translatedCarreras[item.carrera] || item.carrera}</td>
        ),
      },
      { label: table[5]?.label || "Periodo", accessor: "periodo", sortable: true },
    ];
  }, [adminRAttendancePage, translatedApoyos, translatedCondicionesMedicas, translatedCarreras]);

  const cambiarAnio = (e) => {
    let object = { anio: e.target.value, periodoNombre: null };
    cargarPeriodosPorAnio(object);
    setAnio(e.target.value);
  };

  const cargarPeriodosPorAnio = (object) => {
    dispatch(ObtenerTodos(object)).then((d) => {
      if (d?.payload?.length > 0) setPeriodos(d.payload);
    });
  };

  const cambiarPeriodo = (e) => {
    setPeriodoId(e.target.value);
  };

  const exportarSolicitud = async () => {
    await dispatch(exportarEstudiantesConAsistencia({ anio, periodoId }));
  };

  useEffect(() => {
    // if (periodoId && anio)
      dispatch(obtenerEstudiantesConAsistencia({ periodoId, anio })).then(
        (d) => {
          setEstudiantesConAsistencia(d.payload);
        }
      );
  }, [periodoId, anio, dispatch]);

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminRattendancePage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminRAttendancePage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminRAttendancePage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir los campos "Apoyo", "Condición médica" y "Carrera" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasEstudiantes = EstudiantesConAsistencia && Array.isArray(EstudiantesConAsistencia) && EstudiantesConAsistencia.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasEstudiantes) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const apoyoTranslations = {};
      const condicionMedicaTranslations = {};
      const carreraTranslations = {};

      // Traducir solo los campos solicitados
      const translationPromises = EstudiantesConAsistencia.map(async (item) => {
        if (item.apoyoEstudiosNombre) {
          const translated = await _translateValue(SCREEN_NAME, item.apoyoEstudiosNombre, locale);
          apoyoTranslations[item.apoyoEstudiosNombre] = translated;
        }
        if (item.condicionMedica) {
          const translated = await _translateValue(SCREEN_NAME, item.condicionMedica, locale);
          condicionMedicaTranslations[item.condicionMedica] = translated;
        }
        if (item.carrera) {
          const translated = await _translateValue(SCREEN_NAME, item.carrera, locale);
          carreraTranslations[item.carrera] = translated;
        }
      });
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedApoyos(apoyoTranslations);
      setTranslatedCondicionesMedicas(condicionMedicaTranslations);
      setTranslatedCarreras(carreraTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [EstudiantesConAsistencia, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <main className="main-admin">
        <div className="main-admin-bar">
          <Skeleton.Input style={{ width: 300, height: 30 }} active />
          <div className="select-filter-section-ml-auto"></div>
          <div className="header-admin-bar-items ml-auto">
            <Skeleton.Input style={{ width: 60, height: 20 }} active />
            <Skeleton.Input style={{ width: 100, height: 40 }} active />
            <Skeleton.Input style={{ width: 80, height: 20 }} active />
            <Skeleton.Input style={{ width: 100, height: 40 }} active />
          </div>
          <Skeleton.Button style={{ width: 40, height: 40 }} active />
        </div>
        <div className="table-container">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
      </main>
    );
  }

  return (
    <main className="main-admin">
      <div className="main-admin-bar">
        <h2>{adminRAttendancePage?.title || "Estudiantes con asistencia"}</h2>
        <div className="select-filter-section-ml-auto"></div>
        <div className="header-admin-bar-items ml-auto">
          <label>{adminRAttendancePage?.Year || "Año"}</label>
          <select onChange={cambiarAnio}>
            <option value={0}>{adminRAttendancePage?.show_all || "Mostrar todos"}</option>
            {new Array(40).fill(0).map((b, i) => (
              <option key={i} value={new Date().getFullYear() - i}>
                {new Date().getFullYear() - i}
              </option>
            ))}
          </select>
          <label>{adminRAttendancePage?.period || "Periodo"}</label>

          <select onChange={cambiarPeriodo}>
            <option value={0}>{adminRAttendancePage?.show_all || "Mostrar todos"}</option>

            {periodos?.map((item, i) => (
              <option key={i} value={item.periodoId}>
                {item.periodoNombre}
              </option>
            ))}
          </select>
        </div>

        <button
          className="search-button"
          title={adminRAttendancePage?.export_excel_tooltip || "Exportar a excel"}
          onClick={exportarSolicitud}
        >
          <i className="material-icons-outlined"></i>
        </button>
      </div>

      <div className="table-container">
        {EstudiantesConAsistencia?.length > 0 ? (
          <>
            <OrderTable
              {...{ columns }}
              data={EstudiantesConAsistencia}
              clase="table-adm"
            />
          </>
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>
    </main>
  );
}
